import React, { FC, useContext, useMemo } from 'react';

import pluralize from 'pluralize';

import { EvFlexColumn, EvSelectNew } from '@evinced-private/ui-common';

import { FrequencyDto } from '../../interfaces/ScheduledJobConfiguration';

import { SchedulingSectionContext } from './context';
import { CrawlBeforeScanBlock } from './CrawlBeforeScanBlock';
import { schedulingLabels } from './labels';
import { MonthScheduling } from './MonthlyScheduling';
import { NextScanDate } from './NextScanDate';
import { frequencyObject, frequencyOptions } from './SchedulingSections.constants';
import { WeeklyScheduling } from './WeeklyScheduling';

export const SchedulingSectionsMainBlock: FC = () => {
	const { config, updateConfig, isListMode } = useContext(SchedulingSectionContext);
	const { frequency, interval } = config || {};

	const pluralizedFrequencyOptions = useMemo(() => {
		if (!interval) {
			return frequencyOptions;
		}

		return frequencyOptions.map((option) => ({
			...option,
			label: pluralize(option.label, interval)
		}));
	}, [interval]);

	const isNextScanDateVisible = useMemo(() => {
		const { mdays, wdays, frequency, lastDayOfMonth } = config || {};
		if (frequency === FrequencyDto.MONTHLY) {
			return mdays?.length > 0 || lastDayOfMonth;
		}

		return wdays?.length > 0;
	}, [config]);

	return (
		<div className="scheduling-section-content">
			<fieldset className="scheduling-section-period-selects">
				<legend>{schedulingLabels.runEvery}</legend>
				<div className="scheduling-section-period-selects-container">
					<EvSelectNew
						id="scheduling-amount-select"
						className="scheduling-section-period-select"
						options={frequencyObject[frequency]}
						selectedValue={[{ value: interval, label: String(interval) }]}
						onSelectChange={(selectedItem) => {
							updateConfig({ interval: selectedItem[0].value as number });
						}}
						placeholder={schedulingLabels.amount}
						isSearchable={false}
						label={null}
						ariaLabel={schedulingLabels.amount}
					/>
					<EvSelectNew
						id="scheduling-period-type-select"
						className="scheduling-section-period-select"
						options={pluralizedFrequencyOptions}
						selectedValue={pluralizedFrequencyOptions.filter(
							(option) => option.value === frequency
						)}
						onSelectChange={(selectedItem) => {
							const newFrequency = selectedItem[0].value as FrequencyDto;
							const maxInterval = frequencyObject[newFrequency]?.length ?? 1;
							const adjustedInterval = Math.min(interval ?? 1, maxInterval);
							updateConfig({ frequency: newFrequency, interval: adjustedInterval });
						}}
						placeholder={schedulingLabels.periodType}
						isSearchable={false}
						label={null}
						ariaLabel={schedulingLabels.periodType}
					/>
				</div>
			</fieldset>
			<div>{frequency === FrequencyDto.MONTHLY ? <MonthScheduling /> : <WeeklyScheduling />}</div>
			{!isListMode && (
				<EvFlexColumn>
					<CrawlBeforeScanBlock />
				</EvFlexColumn>
			)}
			<div className="scheduling-section-footer">{isNextScanDateVisible && <NextScanDate />}</div>
		</div>
	);
};
